import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { EditFacilityDialog } from '@/components/admin/EditFacilityDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import type { RouterOutput } from '@trapeze/server';
import {
  Building2,
  CheckCircle2,
  Edit,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Unlock,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/facilities')({
  component: FacilitiesPage,
});

type Facility = RouterOutput['admin']['getFacilities']['facilities'][number];

function FacilitiesPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // State
  const [selectedEhrSystem, setSelectedEhrSystem] = useState<'imedicware'>('imedicware');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'private' | 'public'>('all');
  const [selectedFacilities, setSelectedFacilities] = useState<Set<string>>(new Set());
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    facilityId: string;
    facilityName: string | null;
    newStatus: boolean;
  }>({ isOpen: false, facilityId: '', facilityName: null, newStatus: false });
  const [bulkConfirmDialog, setBulkConfirmDialog] = useState<{
    isOpen: boolean;
    newStatus: boolean;
    facilitiesCount: number;
  }>({ isOpen: false, newStatus: false, facilitiesCount: 0 });
  const [editDialog, setEditDialog] = useState<{
    isOpen: boolean;
    facility: Facility | null;
  }>({ isOpen: false, facility: null });

  // Use current organization ID from Clerk
  const currentOrganizationId = organization?.id;

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // tRPC queries
  const facilitiesQuery = trpc.admin.getFacilities.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  // tRPC mutations
  const updatePrivacyMutation = trpc.admin.updateFacilityPrivacy.useMutation({
    onSuccess: () => {
      toast.success('Facility privacy updated successfully');
      facilitiesQuery.refetch();
      setSelectedFacilities(new Set());
    },
    onError: (error) => {
      toast.error(`Failed to update facility privacy: ${error.message}`);
    },
  });

  const bulkUpdateMutation = trpc.admin.bulkUpdateFacilitiesPrivacy.useMutation({
    onSuccess: () => {
      toast.success('Bulk facility privacy update successful');
      facilitiesQuery.refetch();
      setSelectedFacilities(new Set());
    },
    onError: (error) => {
      toast.error(`Failed to bulk update facilities: ${error.message}`);
    },
  });

  const updateFacilityMutation = trpc.admin.updateFacility.useMutation({
    onSuccess: () => {
      toast.success('Facility updated successfully');
      facilitiesQuery.refetch();
      setEditDialog({ isOpen: false, facility: null });
    },
    onError: (error) => {
      toast.error(`Failed to update facility: ${error.message}`);
    },
  });

  const handleEditFacility = (facility: Facility) => {
    setEditDialog({ isOpen: true, facility });
  };

  const handleSaveFacility = (
    facilityId: string,
    data: { address?: string; isPrivateFacility?: boolean }
  ) => {
    updateFacilityMutation.mutate({
      facilityId,
      ...data,
    });
  };

  // Filter and search facilities
  const filteredFacilities = useMemo(() => {
    if (!facilitiesQuery.data?.facilities) return [];

    let filtered = facilitiesQuery.data.facilities;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (facility) =>
          facility.name?.toLowerCase().includes(search) ||
          facility.ehrId.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((facility) => {
        if (filterStatus === 'private') return facility.isPrivateFacility === true;
        if (filterStatus === 'public') return facility.isPrivateFacility !== true;
        return true;
      });
    }

    return filtered;
  }, [facilitiesQuery.data?.facilities, searchTerm, filterStatus]);

  const confirmSingleUpdate = () => {
    updatePrivacyMutation.mutate({
      facilityId: confirmDialog.facilityId,
      isPrivateFacility: confirmDialog.newStatus,
    });
    setConfirmDialog({ isOpen: false, facilityId: '', facilityName: null, newStatus: false });
  };

  const handleBulkUpdate = (isPrivate: boolean) => {
    if (selectedFacilities.size === 0) return;
    setBulkConfirmDialog({
      isOpen: true,
      newStatus: isPrivate,
      facilitiesCount: selectedFacilities.size,
    });
  };

  const confirmBulkUpdate = () => {
    const updates = Array.from(selectedFacilities).map((id) => ({
      facilityId: id,
      isPrivateFacility: bulkConfirmDialog.newStatus,
    }));

    bulkUpdateMutation.mutate({ updates });
    setBulkConfirmDialog({ isOpen: false, newStatus: false, facilitiesCount: 0 });
  };

  const handleSelectAll = () => {
    if (selectedFacilities.size === filteredFacilities.length) {
      setSelectedFacilities(new Set());
    } else {
      setSelectedFacilities(new Set(filteredFacilities.map((f) => f.id)));
    }
  };

  // Loading state
  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Access denied for non-super admins
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Building2 className="h-5 w-5" />
                Access Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                This page is only available to super administrators.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const stats = facilitiesQuery.data?.stats || { total: 0, private: 0, public: 0 };

  return (
    <AdminPageLayout
      title="Facility Management"
      description="Manage facility addresses, privacy settings, and view schedule sync status"
      icon={Building2}
      isSuperAdminPage={true}
    >
      <div className="flex flex-col gap-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Total Facilities</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-orange-600">{stats.private}</div>
              <p className="text-xs text-muted-foreground">Private Facilities</p>
              <p className="text-xs text-muted-foreground mt-1">
                (Surgery centers, appointment details hidden)
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.public}</div>
              <p className="text-xs text-muted-foreground">Public Facilities</p>
              <p className="text-xs text-muted-foreground mt-1">
                (Regular offices, appointments visible)
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Facilities Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Facilities</CardTitle>
                <CardDescription>Configure privacy settings for each facility</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search facilities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 w-64"
                  />
                </div>
                <Select
                  value={filterStatus}
                  onValueChange={(v) => setFilterStatus(v as typeof filterStatus)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Facilities</SelectItem>
                    <SelectItem value="private">Private Only</SelectItem>
                    <SelectItem value="public">Public Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk Actions Bar */}
            {selectedFacilities.size > 0 && (
              <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedFacilities.size} facility(ies) selected
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkUpdate(true)}
                    disabled={bulkUpdateMutation.isPending}
                  >
                    <Lock className="h-4 w-4 mr-1" />
                    Mark as Private
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkUpdate(false)}
                    disabled={bulkUpdateMutation.isPending}
                  >
                    <Unlock className="h-4 w-4 mr-1" />
                    Mark as Public
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedFacilities(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}

            {/* Facilities Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          filteredFacilities.length > 0 &&
                          selectedFacilities.size === filteredFacilities.length
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="min-w-[250px]">Address</TableHead>
                    <TableHead className="hidden md:table-cell">EHR ID</TableHead>
                    <TableHead>Privacy</TableHead>
                    <TableHead>Schedule Sync</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {facilitiesQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : filteredFacilities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No facilities found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFacilities.map((facility) => (
                      <TableRow key={facility.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedFacilities.has(facility.id)}
                            onCheckedChange={(checked) => {
                              const newSet = new Set(selectedFacilities);
                              if (checked) {
                                newSet.add(facility.id);
                              } else {
                                newSet.delete(facility.id);
                              }
                              setSelectedFacilities(newSet);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {facility.name || 'Unnamed Facility'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-xs">
                          <div className="truncate" title={facility.address || undefined}>
                            {facility.address || 'No address'}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground hidden md:table-cell">
                          {facility.ehrId}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={facility.isPrivateFacility ? 'secondary' : 'default'}
                            className="flex items-center gap-1 w-fit"
                          >
                            {facility.isPrivateFacility ? (
                              <>
                                <Lock className="h-3 w-3" />
                                Private
                              </>
                            ) : (
                              <>
                                <Unlock className="h-3 w-3" />
                                Public
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={facility.doctorScheduleSyncEnabled ? 'default' : 'secondary'}
                            className="flex items-center gap-1 w-fit"
                          >
                            {facility.doctorScheduleSyncEnabled ? (
                              <>
                                <RefreshCw className="h-3 w-3" />
                                Enabled
                              </>
                            ) : (
                              <>
                                <X className="h-3 w-3" />
                                Disabled
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => handleEditFacility(facility)}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">About Private Facilities</p>
                  <p className="text-sm text-muted-foreground">
                    Private facilities (typically surgery centers) don't share appointment details
                    with patients through the AI agent. When patients have appointments at these
                    locations, they receive a message that the facility will contact them directly
                    to confirm the date and time.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Single Update Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.isOpen}
        onOpenChange={(open: boolean) =>
          !open &&
          setConfirmDialog({ isOpen: false, facilityId: '', facilityName: null, newStatus: false })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Privacy Change</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <div>
                You are about to change the privacy setting for:
                <div className="font-semibold mt-2">
                  {confirmDialog.facilityName || 'Unnamed Facility'}
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                {confirmDialog.newStatus ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium">
                      <Lock className="h-4 w-4 text-orange-600" />
                      Setting as Private Facility
                    </div>
                    <div className="text-sm text-muted-foreground">
                      This facility will be marked as a surgery center. Appointment details will NOT
                      be shared with patients through the AI agent.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium">
                      <Unlock className="h-4 w-4 text-green-600" />
                      Setting as Public Facility
                    </div>
                    <div className="text-sm text-muted-foreground">
                      This facility will be marked as a regular office. Appointment details WILL be
                      visible to patients through the AI agent.
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSingleUpdate}>Confirm Change</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Update Confirmation Dialog */}
      <AlertDialog
        open={bulkConfirmDialog.isOpen}
        onOpenChange={(open: boolean) =>
          !open && setBulkConfirmDialog({ isOpen: false, newStatus: false, facilitiesCount: 0 })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Privacy Change</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <div>
                You are about to change the privacy setting for{' '}
                <span className="font-semibold">
                  {bulkConfirmDialog.facilitiesCount} facilities
                </span>
                .
              </div>
              <div className="p-3 bg-muted rounded-lg">
                {bulkConfirmDialog.newStatus ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium">
                      <Lock className="h-4 w-4 text-orange-600" />
                      Setting all as Private Facilities
                    </div>
                    <div className="text-sm text-muted-foreground">
                      These facilities will be marked as surgery centers. Appointment details will
                      NOT be shared with patients through the AI agent.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium">
                      <Unlock className="h-4 w-4 text-green-600" />
                      Setting all as Public Facilities
                    </div>
                    <div className="text-sm text-muted-foreground">
                      These facilities will be marked as regular offices. Appointment details WILL
                      be visible to patients through the AI agent.
                    </div>
                  </div>
                )}
              </div>
              <div className="text-sm text-muted-foreground bg-amber-50 dark:bg-amber-950 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                <strong>⚠️ Warning:</strong> This action will affect multiple facilities at once.
                Please review your selection carefully.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkUpdate}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Confirm {bulkConfirmDialog.facilitiesCount} Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Facility Dialog */}
      <EditFacilityDialog
        facility={editDialog.facility}
        open={editDialog.isOpen}
        onOpenChange={(open) => !open && setEditDialog({ isOpen: false, facility: null })}
        onSave={handleSaveFacility}
        isSaving={updateFacilityMutation.isPending}
      />
    </AdminPageLayout>
  );
}
